import React, { useEffect, useState } from 'react';
import { Pencil, Trash2, Plus, List } from 'lucide-react';
import axiosInstance from '../../utils/axiosInstance';
import './AddItemForm.css';
import logo from '../../Assets/logo.jpg';

const AddItemForm = () => {
  const [parents, setParents] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedParent, setSelectedParent] = useState('');
  const [editItemId, setEditItemId] = useState(null);
  const [activeTab, setActiveTab] = useState('add'); // 'add' or 'list'
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    discountPrice: '',
    sku: '',
    stock: '',
    tags: '',
    seoTitle: '',
    seoDescription: '',
    gstRate: '',
    hsnSacCode: '',
    parentCategory: '',
    subCategory: '',
    variants: [], // optional array
    isActive: true,
    image: null,
    vendorEmail: '',
    vendorContact: '',
  });

  useEffect(() => {
    fetchCategories();
    fetchItems();
    fetchVendors();
  }, []);

  const fetchCategories = async () => {
    try {
      const [parentRes, allRes] = await Promise.all([
        axiosInstance.get('/categories-items/all-parent'),
        axiosInstance.get('/categories-items'),
      ]);
      setParents(parentRes.data);
      setAllCategories(allRes.data);
    } catch (err) {
      console.error('Failed to load categories:', err);
      setMessage('❌ Failed to load categories');
    }
  };

  const fetchItems = async () => {
    try {
      const res = await axiosInstance.get('/items');
      setItems(res.data);
    } catch (err) {
      console.error('Failed to fetch items:', err);
      setMessage('❌ Failed to fetch items');
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await axiosInstance.get('/vendor');
      setVendors(res.data);
    } catch (err) {
      console.error('Failed to load vendors:', err);
      setMessage('❌ Failed to load vendors');
    }
  };

  // Update subcategories whenever parent changes
  useEffect(() => {
    const filtered = allCategories.filter(
      (cat) => cat.parentCategoryItem && cat.parentCategoryItem._id === selectedParent
    );
    setSubcategories(filtered);
    setFormData(prev => ({ ...prev, subCategory: '' }));
  }, [selectedParent, allCategories]);

  // Form input change handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'parentCategory') setSelectedParent(value);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (formData.image && formData.image.preview) {
        URL.revokeObjectURL(formData.image.preview);
      }
      setFormData(prev => ({ ...prev, image: { file, preview: URL.createObjectURL(file) } }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      if (!formData.name || !formData.price || !formData.parentCategory || !formData.subCategory) {
        setMessage('❌ Please fill in all required fields');
        setLoading(false);
        return;
      }

      const submitData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (key === 'image' && value.file) {
            submitData.append('image', value.file);
          } else if (key === 'tags') {
            submitData.append('tags', value.split(',').map(tag => tag.trim()));
          } else if (key === 'variants') {
            submitData.append('variants', JSON.stringify(value));
          } else {
            submitData.append(key, value);
          }
        }
      });

      if (editItemId) {
        await axiosInstance.put(`/admin/items/${editItemId}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setMessage('✅ Item updated successfully!');
      } else {
        await axiosInstance.post('/admin/items', submitData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setMessage('✅ Item added successfully!');
      }

      resetForm();
      fetchItems();
      setTimeout(() => setActiveTab('list'), 1500);

    } catch (err) {
      console.error('Error details:', err.response || err);
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
      setMessage(`❌ Failed to save item: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditItemId(item._id);
    setSelectedParent(item.parentCategory?._id || '');
    setFormData({
      name: item.name,
      description: item.description || '',
      price: item.price,
      discountPrice: item.discountPrice || '',
      sku: item.sku || '',
      stock: item.stock || '',
      tags: item.tags?.join(', ') || '',
      seoTitle: item.seoTitle || '',
      seoDescription: item.seoDescription || '',
      gstRate: item.gstRate || '',
      hsnSacCode: item.hsnSacCode || '',
      parentCategory: item.parentCategory?._id || '',
      subCategory: item.subCategory?._id || '',
      variants: item.variants || [],
      isActive: item.isActive,
      image: null,
      vendorEmail: item.vendorEmail || '',
      vendorContact: item.vendorContact || '',
    });
    setActiveTab('add');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await axiosInstance.delete(`/admin/items/${id}`);
      setMessage('✅ Item deleted successfully!');
      fetchItems();
    } catch (err) {
      console.error('Failed to delete item:', err);
      setMessage('❌ Failed to delete item');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      discountPrice: '',
      sku: '',
      stock: '',
      tags: '',
      seoTitle: '',
      seoDescription: '',
      gstRate: '',
      hsnSacCode: '',
      parentCategory: '',
      subCategory: '',
      variants: [],
      isActive: true,
      image: null,
      vendorEmail: '',
      vendorContact: '',
    });
    setSelectedParent('');
    setEditItemId(null);
    setMessage('');
  };

  return (
    <div className="container">
      {/* Tab Navigation */}
      <div className="header">
        <h1 className="title">Item Management</h1>
        <div className="tab-navigation">
          <button
            onClick={() => { setActiveTab('add'); if (!editItemId) resetForm(); }}
            className={`tab-button ${activeTab === 'add' ? 'active' : 'inactive'}`}
          >
            <Plus size={18} /> {editItemId ? 'Edit Item' : 'Add Item'}
          </button>
          <button
            onClick={() => setActiveTab('list')}
            className={`tab-button ${activeTab === 'list' ? 'active' : 'inactive'}`}
          >
            <List size={18} /> Item List ({items.length})
          </button>
        </div>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      {/* Add/Edit Item Form */}
      {activeTab === 'add' && (
        <form className="form-container" onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-section">
              <input type="text" name="name" placeholder="Item Name *" value={formData.name} onChange={handleChange} className="input" required />
              <textarea name="description" placeholder="Description" value={formData.description} onChange={handleChange} className="textarea" rows={3} />
              <input type="number" name="price" placeholder="Price *" value={formData.price} onChange={handleChange} className="input" min="0" step="0.01" required />
              <input type="number" name="discountPrice" placeholder="Discount Price" value={formData.discountPrice} onChange={handleChange} className="input" min="0" step="0.01" />
              <input type="text" name="sku" placeholder="SKU" value={formData.sku} onChange={handleChange} className="input" />
              <input type="number" name="stock" placeholder="Stock" value={formData.stock} onChange={handleChange} className="input" min="0" />
              <input type="text" name="tags" placeholder="Tags (comma separated)" value={formData.tags} onChange={handleChange} className="input" />
              <input type="text" name="seoTitle" placeholder="SEO Title" value={formData.seoTitle} onChange={handleChange} className="input" />
              <input type="text" name="seoDescription" placeholder="SEO Description" value={formData.seoDescription} onChange={handleChange} className="input" />
              <input type="number" name="gstRate" placeholder="GST Rate (%)" value={formData.gstRate} onChange={handleChange} className="input" min="0" step="0.01" />
              <input type="text" name="hsnSacCode" placeholder="HSN/SAC Code" value={formData.hsnSacCode} onChange={handleChange} className="input" />
            </div>

            <div className="form-section">
              <select name="parentCategory" value={formData.parentCategory} onChange={handleChange} className="select" required>
                <option value="">Select Parent Category *</option>
                {parents.map(cat => <option key={cat._id} value={cat._id}>{cat.name}</option>)}
              </select>

              <select name="subCategory" value={formData.subCategory} onChange={handleChange} className="select" required disabled={!selectedParent}>
                <option value="">Select Subcategory *</option>
                {subcategories.map(sub => <option key={sub._id} value={sub._id}>{sub.name}</option>)}
              </select>

              <label>Assign Vendor</label>
              <select
                name="vendorEmail"
                value={formData.vendorEmail}
                onChange={(e) => {
                  const selectedEmail = e.target.value;
                  const vendor = vendors.find(v => v.email === selectedEmail);
                  setFormData(prev => ({ ...prev, vendorEmail: selectedEmail, vendorContact: vendor ? vendor.contactNumber : '' }));
                }}
                className="select"
              >
                <option value="">Select Vendor</option>
                {vendors.map(v => <option key={v._id} value={v.email}>{v.name} ({v.email})</option>)}
              </select>

              <input type="text" name="vendorContact" placeholder="Vendor Contact Number" value={formData.vendorContact} className="input" readOnly />

              <label>Item Image</label>
              <input type="file" name="image" onChange={handleImageChange} className="input" accept="image/*" />
              {formData.image?.preview && <img src={formData.image.preview} alt="Preview" className="preview-image" />}
            </div>
          </div>

          <div className="button-group">
            <button type="submit" disabled={loading} className="button-primary">
              {loading ? 'Saving...' : editItemId ? 'Update Item' : 'Add Item'}
            </button>
            {editItemId && <button type="button" onClick={() => { resetForm(); setActiveTab('list'); }} className="button-secondary">Cancel</button>}
          </div>
        </form>
      )}

      {/* Items List Section */}
      {/* {activeTab === 'list' && (
        <div className="list-container">
          <table className="table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Item Details</th>
                <th>Price</th>
                <th>Category</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan="6">No items found.</td></tr>
              ) : items.map(item => (
                <tr key={item._id}>
                  <td>{item.image?.url ? <img src={item.image.url} alt={item.name} className="item-image" /> : 'No Image'}</td>
                  <td>
                    <strong>{item.name}</strong>
                    {item.description && <p>{item.description}</p>}
                  </td>
                  <td>
                    <img src={logo} alt="currency" style={{ width: '16px', marginRight: '4px' }} />
                    <span>{item.discountPrice ? `${item.discountPrice} (Original: ${item.price})` : item.price}</span>
                  </td>
                  <td>{item.parentCategory?.name} / {item.subCategory?.name}</td>
                  <td><span className={item.isActive ? 'status-active' : 'status-inactive'}>{item.isActive ? 'Active' : 'Inactive'}</span></td>
                  <td>
                    <button onClick={() => handleEdit(item)}><Pencil size={16} /></button>
                    <button onClick={() => handleDelete(item._id)}><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )} */}
      {/* Items List Section */}
{activeTab === 'list' && (
  <div className="list-container">
    <table className="table">
      <thead>
        <tr>
          <th>Image</th>
          <th>Item Details</th>
          <th>Price</th> {/* Original price */}
          <th>Discount Price</th> {/* Discounted price */}
          <th>Category</th>
          <th>Vendor</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {items.length === 0 ? (
          <tr><td colSpan="8">No items found.</td></tr>
        ) : items.map(item => {
          const vendor = vendors.find(v => v.email === item.vendorEmail);
          return (
            <tr key={item._id}>
              <td>
                {item.image?.url 
                  ? <img src={item.image.url} alt={item.name} className="item-image" />
                  : 'No Image'}
              </td>
              <td>
                <strong>{item.name}</strong>
                {item.description && <p>{item.description}</p>}
              </td>
              <td>
                <img src={logo} alt="currency" style={{ width: '16px', marginRight: '4px' }} />
                {item.price}
              </td>
              <td>
                {item.discountPrice ? (
                  <>
                    <img src={logo} alt="currency" style={{ width: '16px', marginRight: '4px' }} />
                    {item.discountPrice}
                  </>
                ) : '-'}
              </td>
              <td>{item.parentCategory?.name} / {item.subCategory?.name}</td>
              <td>
                {vendor ? `${vendor.name} (${vendor.email})` : item.vendorEmail || 'N/A'}
              </td>
              <td>
                <span className={item.isActive ? 'status-active' : 'status-inactive'}>
                  {item.isActive ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td>
                <button onClick={() => handleEdit(item)}><Pencil size={16} /></button>
                <button onClick={() => handleDelete(item._id)}><Trash2 size={16} /></button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
)}

    </div>
  );
};

export default AddItemForm;
